import { Box, IconButton } from '@mui/material';
import BkTable from 'src/components/table/BkTable';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import { useEffect, useState } from 'react';
import {
  removeSuccessMsg,
  loadData,
} from 'src/features/settings/returnAddressSlice';
import { setCurrentUser } from 'src/features/user/userSlice';
import { refreshToken } from 'src/features/user/utils';
import { CurrentUser } from 'src/features/user/constants';
import {
  changeIsDefaultReturnAddress,
  deleteReturnAddress,
} from 'src/features/settings/utils';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import StarIcon from '@mui/icons-material/Star';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { Button } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import { Typography } from 'src/components/new/Typography';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import AddReturnAddressDialog from 'src/components/modals/AddReturnAddressDialog';
import { ReturnAddress } from 'src/utils/constant';
import { addReturnAddressToDb, editReturnAddressInDb } from 'src/features/upload/utils';
import { returnAddressSchema } from 'src/utils/validations';
import { ValidationError } from 'yup';
import { removeReturnErrors as clearErrors, setReturnErrors } from 'src/features/upload/uploadSlice';

interface AddressesBlockProps {
  currentUser: CurrentUser;
}

const columns = [
  { headerName: 'Name', field: 'name' },
  { headerName: 'Firm Name', field: 'firmName'},
  { headerName: 'Address', field: 'address' },
  { headerName: 'City', field: 'city' },
  { headerName: 'State', field: 'state' },
  { headerName: 'ZIP', field: 'zip' },
  { headerName: 'Action', field: 'action' },
];

export const AddressesBlock = ({ currentUser }: AddressesBlockProps) => {
  const dispatch: AppDispatch = useDispatch();

  const returnAddresses = useSelector((state: RootState) => state.user.currentUser.returnAddresses) || [];
  const [editReturnAddress, setEditReturnAddress] = useState<ReturnAddress>();

  const isSettingDefaultAddress = useSelector(
    (state: RootState) => state.settings.returnAddress.isSettingDefaultAddress,
  );

  const [data, setData] = useState<any[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editAddressId, setEditAddressId] = useState(0);

  const [snackBarState, setSnackbarState] = useState<{
    message: string;
    isOpened: boolean;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    message: '',
    isOpened: false,
    severity: 'error',
  });

  useEffect(() => {
    const data = [];
    for (let address of returnAddresses) {
      data.push({
        name: address.addressName,
        firmName: address.firmName,
        address: address.addressOne,
        city: address.city,
        state: address.state,
        zip: address.zipCode,
        action: renderAction(address),
      });
    }
    setData(data);
  }, [currentUser, isSettingDefaultAddress]);

  const onDelete = (id: number) => {
    dispatch(deleteReturnAddress(id)).then((response) => {
      if (response.meta.requestStatus !== 'rejected') {
        dispatch(
          setCurrentUser({
            user: {
              ...currentUser,
              returnAddresses: currentUser.returnAddresses.filter((address) => address.id !== response.payload[0].id),
            },
            accessToken: currentUser.accessToken,
          }),
        );
        dispatch(refreshToken());
      }
    });
  };

  const onMakeDefault = (address: any) => {
    dispatch(loadData({ ...address }));
    setEditAddressId(address.id);
    dispatch(changeIsDefaultReturnAddress(!address.isDefault)).then((response) => {
      if (response.meta.requestStatus !== 'rejected') {
        dispatch(
          setCurrentUser({
            user: {
              ...currentUser,
              returnAddresses: currentUser.returnAddresses.map((returnAddress) =>
                returnAddress.id === address.id
                  ? { ...returnAddress, isDefault: true }
                  : { ...returnAddress, isDefault: false },
              ),
            },
            accessToken: currentUser.accessToken,
          }),
        );
        dispatch(refreshToken());
      }
    });
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setEditAddressId(0);
    setEditReturnAddress(undefined);
  };

  const openReturnAddressDialog = (address: any) => {
    dispatch(loadData({ ...address }));
    setEditAddressId(address.id);
    setEditReturnAddress(address);
    setShowDrawer(true);
    dispatch(clearErrors());
    dispatch(removeSuccessMsg());
  };

  const openAddDialog = () => {
    setShowDrawer(true);
    dispatch(loadData({}));
    dispatch(clearErrors());
    dispatch(removeSuccessMsg());
  };

  const validateAddress = async (returnAddress: ReturnAddress) => {
    try {
      await returnAddressSchema.validate(returnAddress, {
        abortEarly: false,
      });

      return true;
    } catch (err) {
      if (err instanceof ValidationError) {
        const validationErrors: { [key: string]: string } = {};

        err.inner.forEach((error) => {
          validationErrors[error.path || ''] = error.message;
        });

        dispatch(setReturnErrors(validationErrors));
        return false;
      } else {
        console.error('Unexpected error:', err);
      }
    }
  };

  const addReturnAddress = async (
    returnAddress: ReturnAddress,
    action: 'edit' | 'create',
  ) => {
    const returnAddressArrayCopy = [...returnAddresses];

    let isValid = await validateAddress(returnAddress);

    if (isValid) {
      if (action === 'create') {
        returnAddressArrayCopy.push(returnAddress);

        const address = { ...returnAddress, selected: true };

        dispatch(addReturnAddressToDb(address)).then((response) => {
          if (response.meta.requestStatus !== 'rejected') {
            dispatch(
              setCurrentUser({
                user: {
                  ...currentUser,
                  returnAddresses: [
                    ...currentUser.returnAddresses,
                    response.payload,
                  ],
                },
                accessToken: currentUser.accessToken,
              }),
            );
            dispatch(refreshToken());
            setSnackbarState({
              ...snackBarState,
              isOpened: true,
              message: 'Address edited succesfully!',
              severity: 'success',
            });
          } else {
            setSnackbarState({
              ...snackBarState,
              isOpened: true,
              message: 'Error occured during address update',
              severity: 'error',
            });
          }
        });
      } else {
        onEditReturnAddress(returnAddress);
      }

      closeDrawer();
    }
  };

  const onEditReturnAddress = (returnAddress: ReturnAddress) => {
    dispatch(editReturnAddressInDb(returnAddress)).then((response) => {
      if (response.meta.requestStatus !== 'rejected') {
        dispatch(
          setCurrentUser({
            user: {
              ...currentUser,
              returnAddresses: currentUser.returnAddresses.map((address) =>
                address.id === returnAddress.id
                  ? returnAddress
                  : address,
              ),
            },
            accessToken: currentUser.accessToken,
          }),
        );
        dispatch(refreshToken());

        setSnackbarState({
          ...snackBarState,
          isOpened: true,
          message: 'Address edited succesfully!',
          severity: 'success',
        });
      } else {
        setSnackbarState({
          ...snackBarState,
          isOpened: true,
          message: 'Error occured during address update',
          severity: 'error',
        });
      }
    });

    closeDrawer();
  };

  const renderAction = (address: any) => {
    return (
      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <IconButton onClick={() => onMakeDefault(address)} disabled={address.isDefault}>
            {address.isDefault ? <StarIcon sx={{ color: '#436EB2' }} /> : <StarBorderOutlinedIcon />}
          </IconButton>
          {isSettingDefaultAddress &&
            editAddressId === address.id &&
            Array.from({ length: 6 }).map((_, index) => {
              const angle = (index / 6) * 2 * Math.PI;
              const x = Math.cos(angle) * 30;
              const y = Math.sin(angle) * 30;
              return (
                <div
                  key={index}
                  style={
                    {
                      position: 'absolute',
                      top: '40%',
                      left: '40%',
                      width: '0.625rem',
                      height: '0.625rem',
                      backgroundColor: 'rgb(0, 75, 187)',
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 0,
                      animation: 'particle-burst 1s ease-out forwards',
                      transformOrigin: 'center',
                      '--x-offset': `${x}px`,
                      '--y-offset': `${y}px`,
                    } as React.CSSProperties
                  }
                />
              );
            })}
        </div>
        <IconButton onClick={() => openReturnAddressDialog(address)}>
          <CreateOutlinedIcon />
        </IconButton>
        <IconButton disabled={address.isDefault} onClick={() => onDelete(address.id)}>
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </div>
    );
  };

  return (
    <Box sx={{ padding: '2.5rem 1.5rem' }}>
      <Typography sx={{ fontSize: '2rem', lineHeight: '2.5rem' }} fontWeight={400}>
        Return Address
      </Typography>
      <Spacer h={24} />
      {returnAddresses.length > 0 && (
        <BkTable data={data} columns={columns} initialRowsPerPage={10} renderPagination={false} />
      )}
      <Box sx={{ maxWidth: '25rem', marginTop: '1.5rem' }}>
        <Button type="outline" onClick={openAddDialog}>
          <AddOutlinedIcon />
          <Spacer w={8} />
          Add Address
        </Button>
      </Box>
      <AddReturnAddressDialog
        open={showDrawer}
        onClose={closeDrawer}
        onSubmit={addReturnAddress}
        isEditing={editAddressId ? true : false}
        itemToEdit={editReturnAddress}
        returnAddrStatus={null}
      />
      <BkSnackBar
        open={snackBarState.isOpened}
        handleCloseSnackbar={() => setSnackbarState({ ...snackBarState, isOpened: false })}
        snackMessage={snackBarState.message}
        severity={snackBarState.severity}
      />
    </Box>
  );
};
